// ============================================================================
// FLAG RESOLUTION MODAL COMPONENT
// ============================================================================
// Modal dialog for reviewing and resolving security flags with notes.
// ============================================================================

import React, { useState } from 'react';
import { 
  X, AlertTriangle, CheckCircle, XCircle, HelpCircle,
  Clock, User, Users, FileText, Shield
} from 'lucide-react';
import { securityFlagService } from '../../../services/security';
import { 
  SecurityFlag, 
  FlagResolution, 
  ActionTaken,
  FlagSeverity,
  SecurityFlagType
} from '../../../types/securityTypes';

// ============================================================================
// INTERFACES
// ============================================================================

interface FlagResolutionModalProps {
  flag: SecurityFlag;
  isOpen: boolean;
  onClose: () => void;
  onResolved: (flagId: string, resolution: FlagResolution, notes: string, action: ActionTaken) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const SEVERITY_COLORS: Record<FlagSeverity, string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const TYPE_LABELS: Record<SecurityFlagType, string> = {
  collusion: 'Collusion Detection',
  multi_account: 'Multi-Account Violation',
  bot: 'Bot Detection',
  chip_dumping: 'Chip Dumping',
  same_ip_table: 'Same IP at Table',
  suspicious_timing: 'Suspicious Timing',
  unusual_pattern: 'Unusual Pattern',
};

const TYPE_DESCRIPTIONS: Record<SecurityFlagType, string> = {
  collusion: 'Two or more players suspected of coordinating play to gain unfair advantage.',
  multi_account: 'Multiple accounts detected on same device or with linked behavior.',
  bot: 'Automated or bot-like behavior patterns detected in gameplay.',
  chip_dumping: 'Deliberate transfer of chips between accounts detected.',
  same_ip_table: 'Multiple players at the same table sharing an IP address.',
  suspicious_timing: 'Unusual action timing patterns that may indicate automated play.',
  unusual_pattern: 'Gameplay patterns that deviate significantly from normal behavior.',
};

const RESOLUTION_OPTIONS: { value: FlagResolution; label: string; icon: React.ReactNode; description: string }[] = [
  {
    value: 'confirmed',
    label: 'Confirmed Violation',
    icon: <AlertTriangle className="w-5 h-5 text-red-400" />,
    description: 'The flagged behavior is a confirmed violation of terms.',
  },
  {
    value: 'false_positive',
    label: 'False Positive',
    icon: <CheckCircle className="w-5 h-5 text-green-400" />,
    description: 'The flag was triggered incorrectly; no violation occurred.',
  },
  {
    value: 'inconclusive',
    label: 'Inconclusive',
    icon: <HelpCircle className="w-5 h-5 text-yellow-400" />,
    description: 'Unable to determine if a violation occurred; requires monitoring.',
  },
];

const ACTION_OPTIONS: { value: ActionTaken; label: string; description: string; color: string }[] = [
  {
    value: 'none',
    label: 'No Action',
    description: 'Resolve without taking any action against the account.',
    color: 'bg-gray-700 hover:bg-gray-600',
  },
  {
    value: 'warning',
    label: 'Issue Warning',
    description: 'Send a warning to the user about their behavior.',
    color: 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400',
  },
  {
    value: 'hold',
    label: 'Place Hold',
    description: 'Place a temporary hold on withdrawals pending review.',
    color: 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-400',
  },
  {
    value: 'suspend',
    label: 'Suspend Account',
    description: 'Temporarily suspend the account pending investigation.',
    color: 'bg-red-500/20 hover:bg-red-500/30 text-red-400',
  },
  {
    value: 'ban',
    label: 'Ban Account',
    description: 'Permanently ban the account from the platform.',
    color: 'bg-red-600/30 hover:bg-red-600/40 text-red-400',
  },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FlagResolutionModal: React.FC<FlagResolutionModalProps> = ({
  flag,
  isOpen,
  onClose,
  onResolved,
}) => {
  const [resolution, setResolution] = useState<FlagResolution | null>(null);
  const [action, setAction] = useState<ActionTaken>('none');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSubmit = async () => {
    if (!resolution) {
      setError('Please select a resolution');
      return;
    }

    if (!notes.trim()) {
      setError('Please add resolution notes');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await securityFlagService.resolveFlag(
        flag.id!,
        resolution,
        notes,
        action
      );

      onResolved(flag.id!, resolution, notes, action);
      onClose();
    } catch (err) {
      console.error('Failed to resolve flag:', err);
      setError('Failed to resolve flag. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 rounded-xl border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-400" />
            <h2 className="text-lg font-bold text-white">Resolve Security Flag</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Flag Details */}
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-lg text-sm font-medium border ${SEVERITY_COLORS[flag.severity]}`}>
                  {flag.severity.toUpperCase()}
                </span>
                <span className="text-sm text-gray-400">{TYPE_LABELS[flag.type]}</span>
              </div>
              <span className="text-xs text-gray-500">
                {formatTime(flag.createdAt)}
              </span>
            </div>

            <p className="text-white mb-4">{flag.description}</p>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <User className="w-4 h-4" />
                <span>Primary: {flag.primaryUserId.substring(0, 12)}...</span>
              </div>
              {flag.relatedUserIds && flag.relatedUserIds.length > 0 && (
                <div className="flex items-center gap-2 text-gray-400">
                  <Users className="w-4 h-4" />
                  <span>{flag.relatedUserIds.length} related user(s)</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="w-4 h-4" />
                <span>Confidence: {(flag.confidence * 100).toFixed(0)}%</span>
              </div>
            </div>

            {/* Type Description */}
            <div className="mt-4 p-3 bg-gray-900/50 rounded-lg">
              <p className="text-xs text-gray-500">{TYPE_DESCRIPTIONS[flag.type]}</p>
            </div>

            {/* Evidence */}
            {flag.evidence && Object.keys(flag.evidence).length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Evidence
                </p>
                <pre className="p-3 bg-gray-900 rounded-lg text-xs text-gray-300 overflow-x-auto">
                  {JSON.stringify(flag.evidence, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Resolution Selection */}
          <div>
            <h3 className="text-sm font-medium text-gray-300 mb-3">Resolution</h3>
            <div className="space-y-2">
              {RESOLUTION_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                    resolution === option.value
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="resolution"
                    value={option.value}
                    checked={resolution === option.value}
                    onChange={() => setResolution(option.value)}
                    className="sr-only"
                  />
                  <div className="flex-shrink-0 mt-0.5">{option.icon}</div>
                  <div>
                    <p className="font-medium text-white">{option.label}</p>
                    <p className="text-sm text-gray-400">{option.description}</p>
                  </div>
                  {resolution === option.value && (
                    <CheckCircle className="w-5 h-5 text-blue-400 ml-auto flex-shrink-0" />
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Action Selection (only shown if resolution is 'confirmed') */}
          {resolution === 'confirmed' && (
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Action to Take</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ACTION_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setAction(option.value)}
                    className={`p-3 rounded-lg border text-sm text-left transition-all ${
                      action === option.value
                        ? 'border-blue-500 ring-1 ring-blue-500/50'
                        : 'border-gray-700 hover:border-gray-600'
                    } ${option.color}`}
                  >
                    <p className="font-medium">{option.label}</p>
                    <p className="text-xs opacity-70 mt-1">{option.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <h3 className="text-sm font-medium text-gray-300 mb-3">
              Resolution Notes <span className="text-red-400">*</span>
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe your findings and reasoning for this resolution..."
              className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-700 p-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !resolution || !notes.trim()}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              isSubmitting || !resolution || !notes.trim()
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
          >
            {isSubmitting ? 'Resolving...' : 'Resolve Flag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagResolutionModal;